import React, { useEffect, useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';

const steps = [
  {
    key: 'install',
    title: 'Add App to Home Screen (Optional)',
    detail: 'Use your browser menu to add 70Lives to your home screen for faster access. Works on Safari and Chrome.'
  },
  {
    key: 'drive',
    title: 'Connect Google Drive',
    detail: 'Back up your data and sync across devices.'
  },
  {
    key: 'basics',
    title: 'Confirm Business Setup',
    detail: 'Choose your country and currency, business structure, and tax registration status before using tax and reporting tools.'
  }
];

const SetupChecklistModal = ({
  isOpen,
  onClose,
  progress,
  onToggleStep,
  onInstall,
  onBasics
}) => {
  const initialIndex = useMemo(() => {
    if (progress?.install !== true) return 0;
    if (progress?.drive !== true) return 1;
    if (progress?.basics !== true) return 2;
    return 2;
  }, [progress]);

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentStep = steps[currentIndex] || steps[0];
  const isLastStep = currentIndex === steps.length - 1;
  const driveAlreadyConnected = currentStep.key === 'drive' && progress?.drive === true;

  useEffect(() => {
    if (!isOpen) return;
    setCurrentIndex(initialIndex);
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (driveAlreadyConnected) {
      onToggleStep?.('drive', true);
      const timer = setTimeout(() => {
        setCurrentIndex((idx) => Math.min(idx + 1, steps.length - 1));
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [driveAlreadyConnected, onToggleStep]);

  if (!isOpen) return null;

  const advanceStep = () => {
    if (currentIndex < steps.length - 1) {
      setCurrentIndex((idx) => idx + 1);
    } else {
      onClose?.();
    }
  };

  const handleMarkDone = () => {
    onToggleStep?.(currentStep.key, true);
    if (isLastStep) {
      onClose?.();
    } else {
      advanceStep();
    }
  };

  const handleSkip = () => {
    advanceStep();
  };

  const handleBack = () => {
    setCurrentIndex((idx) => Math.max(0, idx - 1));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1100] flex items-center justify-center px-4">
      <div className="bg-info-gradient rounded-2xl shadow-2xl max-w-xl w-full p-6 text-[color:var(--color-text)] space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase font-semibold tracking-wide text-[color:var(--color-text)]/70">
              Setup Your Business (3 Steps) - Step {currentIndex + 1} of {steps.length}
            </p>
            <h3 className="text-2xl font-semibold text-[color:var(--color-text)]">Let's get you set up</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/60 text-[color:var(--color-text)]"
            aria-label="Close setup checklist"
          >
            <X size={18} />
          </button>
        </div>

        <div className="border border-black rounded-2xl bg-accent-gradient p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                progress?.[currentStep.key]
                  ? 'bg-[color:var(--color-warn)] border-black text-[color:var(--color-text)]'
                  : 'bg-warn-gradient border-black text-transparent'
              }`}
            >
              {progress?.[currentStep.key] && <Check size={13} />}
            </div>
            <div className="space-y-2">
              <p className="font-semibold text-[color:var(--color-text)] text-lg leading-snug">{currentStep.title}</p>
              <p className="text-sm text-[color:var(--color-text)]/80">{currentStep.detail}</p>

              {currentStep.key === 'install' && (
                <div className="text-xs text-[color:var(--color-text)]/75 mt-1 bg-white/70 px-3 py-2 rounded-lg border border-black/10">
                  Installation is optional. You can still use all features directly in your browser.
                </div>
              )}

              {currentStep.key === 'drive' && progress?.drive !== true && (
                <ul className="text-xs text-[color:var(--color-text)]/75 list-disc pl-4 mt-1 space-y-1">
                  <li>Connect once to enable cloud backup and device sync.</li>
                  <li>Local changes sync after reconnect when Drive is available again.</li>
                </ul>
              )}

              {driveAlreadyConnected && (
                <div className="text-xs text-[color:var(--color-text)]/80 mt-1 bg-white/70 px-3 py-2 rounded-lg border border-black/10">
                  Drive is already connected. Moving to the next step.
                </div>
              )}

              {currentStep.key === 'basics' && (
                <div className="text-xs text-[color:var(--color-text)]/80 mt-2 bg-white/70 px-3 py-2 rounded-lg border border-black/10">
                  Complete the required setup prompt first. The guided walkthrough remains available from the dashboard checklist afterward.
                  {typeof onBasics === 'function' && (
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={() => onBasics()}
                        className="px-3 py-1.5 rounded-lg bg-warn-gradient text-[color:var(--color-text)] font-semibold hover:brightness-95"
                      >
                        Open guide
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 justify-center max-w-2xl mx-auto w-full">
          {currentStep.key === 'install' ? (
            <button
              type="button"
              onClick={() => onInstall?.()}
              disabled={!onInstall}
              className="flex-none w-40 px-4 py-2 bg-[color:var(--color-accent)] text-[color:var(--color-text)] rounded-lg hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed text-center whitespace-nowrap"
            >
              Install
            </button>
          ) : (
            <button
              type="button"
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="flex-none w-40 px-4 py-2 text-[color:var(--color-text)]/80 rounded-lg hover:bg-white/60 disabled:opacity-50 disabled:cursor-not-allowed text-center whitespace-nowrap"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={handleSkip}
            className="flex-none w-40 px-4 py-2 text-[color:var(--color-text)]/80 rounded-lg hover:bg-white/60 text-center whitespace-nowrap"
          >
            Skip for now
          </button>
          <button
            type="button"
            onClick={handleMarkDone}
            className="flex-none w-40 px-4 py-2 bg-warn-gradient text-[color:var(--color-text)] rounded-lg hover:brightness-95 text-center whitespace-nowrap"
          >
            {isLastStep ? 'Finish' : 'Mark as done'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupChecklistModal;
